import { approach, clamp, clamp01, damp, smoothstep } from '../core/math';
import type { Body } from '../physics/body';
import type { VehicleControls, VehicleDamage, VehicleSpec, VehicleTelemetry } from './types';

/**
 * Arcade "two-axle impulse" vehicle model. Pure function of (body, spec, controls, mods, dt) —
 * no rendering, no globals — so it is deterministic and unit-testable.
 *
 * Each fixed step:
 *  1. Steering: input slews at `steerSpeed`; max wheel angle shrinks with speed (stable at speed,
 *     sharp in town); a counter-steer assist turns the front wheels into slides.
 *  2. Longitudinal: engine curve to top speed, brakes, reverse, engine braking, drag, rolling
 *     resistance, handbrake. Applied as a velocity change along the heading.
 *  3. Lateral: at each axle we compute the impulse that would cancel that axle's sideways velocity
 *     (through the body's effective mass at that point, so it also produces yaw) and clamp it to
 *     μ·N·dt of that axle. Inside the limit the tyre grips; beyond it the axle slides. Separate front
 *     and rear limits give natural understeer/oversteer; the handbrake slashes rear μ; throttle eats
 *     the driven axle's grip (friction circle) for power oversteer. Load transfer moves N between
 *     axles under braking/acceleration.
 * Collisions are then resolved by Physics2D on top of the result.
 */

const G = 9.81;
const MAX_YAW_RATE = 5.5;
const YAW_AGILITY = 1.7;
const HANDBRAKE_YAW = 11;

/**
 * The player's drift layer (`ModelModifiers.drift`, docs/DRIVING.md §1 "Default drift"). Each is the
 * full-strength value, scaled by `drift` (0 for AI drivers, so their lines see the plain grip model).
 */
/** Rear grip given up for a gentle default oversteer (fraction of rearGrip). */
const DRIFT_REAR_LOSS = 0.08;
/**
 * Extra rear grip full throttle takes away at town speeds, on every drivetrain: this × (0.2 + the spec's
 * powerOversteer, capped at 0.4), so the saloons step out more than the van, cab, lorry or FWD Minnow.
 */
const DRIFT_THROTTLE_LOSS = 0.75;
/** The rear-grip part of the drift (loss and knee) fades in from this speed (m/s) to 5 m/s above it. */
const DRIFT_FROM_SPEED = 7;
/** Rear tyre: the force rounds off from this fraction of the peak instead of hitting a hard ceiling. */
const DRIFT_KNEE = 0.66;
/** Slip angle (rad, ~28°) the drift assist holds a slide to; beyond it the car is pulled back. */
const DRIFT_MAX_SLIP = 0.48;
/** With the handbrake held the slide may open a little further (rad, ~50°) before it is pulled back. */
const DRIFT_MAX_SLIP_HANDBRAKE = 0.88;
/** How hard (1/s) slip beyond the limit is turned back into forward speed, and yaw feeding it damped. */
const DRIFT_HOLD = 12;
/** How fast (1/s) a drift under the limit is carried round towards the nose (scaled by the drift window). */
const DRIFT_CARRY = 1.5;
/** Counter-steer assist gain multiplier and extra cap (rad) for the player. */
const DRIFT_COUNTER = 0.5;
const DRIFT_COUNTER_CAP = 0.1;
/** Snappier turn-in for the player: a little more yaw agility. */
const DRIFT_AGILITY = 0.35;
/** Snappier turn-in for the player: the wheel slews this much faster (fraction of the spec's steerSpeed). */
const DRIFT_STEER_SPEED = 0.35;
/** Milder handbrake: the rear keeps this share of the grip the spec's handbrakeGrip would take away. */
const DRIFT_HANDBRAKE_KEEP = 0.45;
/** Forward handbrake yaw kick: strength and yaw-rate target scale (J-turns in reverse keep the full kick). */
const DRIFT_HANDBRAKE_KICK = 0.45;

export function makeTelemetry(): VehicleTelemetry {
  return {
    forwardSpeed: 0,
    lateralSpeed: 0,
    skid: 0,
    wheelSkid: [0, 0, 0, 0],
    frontSlip: 0,
    rearSlip: 0,
    wheelspin: 0,
    braking: false,
    reversing: false,
    steer: 0,
    wheelAngle: 0,
    longAccel: 0,
    latAccel: 0,
    gear: 1,
    rpm: 0,
    load: 0,
    throttle: 0,
    scrape: 0,
    impact: 0,
    topSpeed: 0,
  };
}

export interface ModelModifiers {
  /** 0..1 engine output (damage / destroyed). */
  power: number;
  /** 0..1 multiplier on top speed. */
  topSpeed: number;
  /** Constant steering pull from bent suspension, in steer units (+ = right). */
  steerPull: number;
  /** 0..1 multiplier on tyre grip (bent wheels). */
  grip: number;
  /**
   * 0..1 arcade drift layer (docs/DRIVING.md §1): 1 for the player's car, 0 (or absent) for AI drivers,
   * whose lines are tuned for the plain grip model.
   */
  drift?: number;
}

export const FULL_HEALTH_MODS: ModelModifiers = { power: 1, topSpeed: 1, steerPull: 0, grip: 1 };
/** Full health, driven by the player (the drift layer on). */
export const PLAYER_MODS: ModelModifiers = { ...FULL_HEALTH_MODS, drift: 1 };

/** How damage degrades handling. Kept separate (and pure) so it can be unit-tested. */
export function damageModifiers(spec: VehicleSpec, health: number, damage: VehicleDamage, destroyed: boolean): ModelModifiers {
  if (destroyed) return { power: 0, topSpeed: 0.3, steerPull: 0, grip: 0.7 };
  const h = clamp01(health / spec.maxHealth);
  const wear = 1 - h;
  const power = clamp(1 - damage.front * 0.3 - (h < 0.25 ? 0.25 : 0), 0.35, 1);
  const topSpeed = clamp(1 - damage.front * 0.25 - wear * wear * 0.35, 0.45, 1);
  const steerPull = clamp((damage.right - damage.left) * 0.14, -0.14, 0.14);
  const grip = clamp(1 - Math.max(damage.left, damage.right, damage.rear) * 0.12, 0.8, 1);
  return { power, topSpeed, steerPull, grip };
}

/** Engine curve: full pull at standstill, tapering to 0 at top speed, negative above it. */
function powerCurve(v: number, top: number): number {
  const x = v / Math.max(top, 0.1);
  if (x <= 0) return 1;
  if (x >= 1) return -(x - 1) * 3;
  return 1 - x * x * x;
}

/** Slip angle (rad) at which a tyre's force peaks: stiffer tyres peak earlier. */
function tyrePeak(spec: VehicleSpec): number {
  return 0.06 + (1 - spec.tyreStiffness) * 0.16;
}

/**
 * Full-lock front wheel angle (rad) at `speed` (m/s): it shrinks with speed (`steerFalloffSpeed`, floored
 * at `minSteer`) and is capped by a grip-aware lock, so at speed full lock asks for roughly the front
 * axle's grip limit (plus a bit of overdrive for bite) instead of ploughing the front tyres far past
 * their peak. Exported for the touch steering (src/input/touch.ts), which needs the yaw rate full lock
 * gives to aim the car without overshoot.
 */
export function steerLock(spec: VehicleSpec, speed: number): number {
  const falloff = Math.max(spec.minSteer, 1 / (1 + (speed / spec.steerFalloffSpeed) ** 2));
  const gripLock = speed > 1 ? (spec.wheelbase * spec.frontGrip * G * 1.15) / (speed * speed) + tyrePeak(spec) * 1.3 : Infinity;
  return Math.min(spec.steerAngle * falloff, gripLock);
}

/** Yaw rate (rad/s) full lock asks for at `speed` on grip (kinematic: v·tan(lock)/wheelbase). */
export function fullLockYawRate(spec: VehicleSpec, speed: number): number {
  return (Math.abs(speed) * Math.tan(steerLock(spec, speed))) / spec.wheelbase;
}

export function stepVehicleModel(
  body: Body,
  spec: VehicleSpec,
  controls: VehicleControls,
  tel: VehicleTelemetry,
  mods: ModelModifiers,
  dt: number,
): void {
  const throttle = clamp(controls.throttle, -1, 1);
  const handbrake = controls.handbrake;
  const steerIn = clamp(controls.steer + mods.steerPull, -1, 1);
  const drift = clamp01(mods.drift ?? 0);
  const m = body.mass;
  const invM = body.invMass;
  const invI = body.invInertia;

  const sA = Math.sin(body.angle);
  const cA = Math.cos(body.angle);
  // forward = (sA, cA), right = (-cA, sA)
  const vF0 = body.vel.x * sA + body.vel.z * cA;
  const vR0 = -body.vel.x * cA + body.vel.z * sA;
  // sqrt, not Math.hypot: V8's hypot boxes its arguments (garbage at 60 Hz per car; docs/PERF.md "Phone pass").
  const speed = Math.sqrt(vF0 * vF0 + vR0 * vR0);

  // ------------------------------------------------------------------ steering
  const returning = steerIn === 0 || Math.sign(steerIn) !== Math.sign(tel.steer);
  tel.steer = approach(tel.steer, steerIn, spec.steerSpeed * (1 + DRIFT_STEER_SPEED * drift) * (returning ? 1.8 : 1) * dt);
  const peak = tyrePeak(spec);
  let delta = tel.steer * steerLock(spec, speed);
  // Counter-steer assist: steer toward the direction of travel when the car is sliding forwards.
  if (!handbrake && vF0 > 4) {
    const beta = Math.atan2(vR0, vF0);
    const cap = 0.3 + DRIFT_COUNTER_CAP * drift;
    delta += clamp(beta * spec.counterSteer * (1 + DRIFT_COUNTER * drift), -cap, cap);
  }
  delta = clamp(delta, -spec.steerAngle, spec.steerAngle);
  tel.wheelAngle = delta;

  // ------------------------------------------------------------------ axle loads
  const transfer = clamp((tel.longAccel * spec.weightTransfer) / G, -0.22, 0.08);
  const frontFrac = clamp(spec.frontWeight - transfer, 0.25, 0.75);
  const nF = m * G * frontFrac;
  const nR = m * G * (1 - frontFrac);

  // ------------------------------------------------------------------ longitudinal
  const top = spec.maxSpeed * mods.topSpeed;
  let drive = 0; // signed acceleration from the engine
  let brake = 0; // magnitude, always opposes motion
  let braking = false;
  if (throttle > 0) {
    if (vF0 < -0.5) {
      brake += spec.brake * throttle;
      braking = true;
    } else drive = spec.accel * throttle * mods.power * powerCurve(vF0, top);
  } else if (throttle < 0) {
    if (vF0 > 0.5) {
      brake += spec.brake * -throttle;
      braking = true;
    } else drive = -spec.accel * 0.7 * -throttle * mods.power * powerCurve(-vF0, spec.maxReverse);
  } else if (Math.abs(vF0) > 0.1) {
    brake += spec.engineBrake * (0.35 + (0.65 * Math.abs(vF0)) / spec.maxSpeed);
  }
  if (handbrake) {
    brake += spec.brake * 0.45;
    braking = braking || Math.abs(vF0) > 0.5;
  }
  brake += spec.rolling + spec.drag * vF0 * vF0;

  // Traction: arcade cap on drive force; excess becomes wheelspin.
  const driveMu = spec.drive === 'rear' ? spec.rearGrip : spec.frontGrip;
  const tractionCap = driveMu * G * 1.15 * mods.grip;
  const usedTraction = clamp01(Math.abs(drive) / tractionCap);
  drive = clamp(drive, -tractionCap, tractionCap);

  let vF = vF0 + drive * dt;
  const dec = brake * dt;
  if (Math.abs(vF) <= dec) vF = drive !== 0 ? vF - Math.sign(vF) * Math.min(Math.abs(vF), dec) : 0;
  else vF -= Math.sign(vF) * dec;
  const dvF = vF - vF0;
  body.vel.x += sA * dvF;
  body.vel.z += cA * dvF;

  // ------------------------------------------------------------------ lateral (per-axle impulses)
  const a = spec.wheelbase * (1 - spec.frontWeight); // CG -> front axle
  const b = spec.wheelbase * spec.frontWeight; // CG -> rear axle
  const rfx = sA * a;
  const rfz = cA * a;
  const rrx = -sA * b;
  const rrz = -cA * b;
  // Lateral axis of the front wheels (heading - delta) and rear wheels (heading).
  const wA = body.angle - delta;
  const nfx = -Math.cos(wA);
  const nfz = Math.sin(wA);
  const nrx = -cA;
  const nrz = sA;
  // Friction circle on the driven axle: throttle steals lateral grip (power oversteer on RWD,
  // power understeer on FWD). Strongest at town speeds, fades out at high speed for stability.
  const speedWindow = smoothstep(2, 6, speed) * (1 - smoothstep(26, 45, speed));
  const thr = Math.max(throttle, 0) * mods.power;
  const throttleLoss = spec.powerOversteer * thr * 0.32 * speedWindow;
  const circle = (1 - throttleLoss) * Math.sqrt(Math.max(0.05, 1 - (usedTraction * 0.3) ** 2));
  let muF = spec.frontGrip * mods.grip * (spec.drive === 'front' ? circle : 1);
  let muR = spec.rearGrip * mods.grip * (spec.drive === 'rear' ? circle : 1);
  // Drift layer: the rear gives a little, more under power at town speeds, so a corner taken on the
  // throttle ends in a short slide (the slip cap below keeps it from becoming a spin).
  // Not below ~8 m/s: an alley mouth, a U-turn or pulling out of a junction on full throttle should go
  // where the car points, not slide wide into the kerb (controls route: the ginnel, the hairpin).
  const driftWindow = drift * smoothstep(DRIFT_FROM_SPEED, DRIFT_FROM_SPEED + 5, speed) * (1 - smoothstep(26, 45, speed));
  if (driftWindow > 0) muR *= 1 - driftWindow * (DRIFT_REAR_LOSS + DRIFT_THROTTLE_LOSS * (0.2 + Math.min(spec.powerOversteer, 0.4)) * thr);
  if (handbrake) muR *= spec.handbrakeGrip + (1 - spec.handbrakeGrip) * DRIFT_HANDBRAKE_KEEP * drift;
  // Low speed: tyres always grip (no parking-lot drifting).
  if (speed < 3) {
    muF = Math.max(muF, 1.2);
    muR = Math.max(muR, handbrake ? 0.6 : 1.2);
  }
  const maxF = muF * nF * dt;
  const maxR = muR * nR * dt;
  // Tyre curve: linear in slip angle up to `peak`, then saturates at μN. Stiffer tyres peak earlier.
  // Arcade cheat: tyre forces see a lower yaw inertia than collisions do, so cars rotate eagerly
  // on turn-in but still feel weighty when shunted.
  const invIt = invI * (YAW_AGILITY + DRIFT_AGILITY * drift);
  const cfz = rfz * nfx - rfx * nfz; // crossY(rf, nf)
  const crz = rrz * nrx - rrx * nrz; // crossY(rr, nr)
  const kF = invM + invIt * cfz * cfz;
  const kR = invM + invIt * crz * crz;
  // Axle velocities (pre-state) -> slip.
  const fvx = body.vel.x + body.angVel * rfz;
  const fvz = body.vel.z - body.angVel * rfx;
  const rvx = body.vel.x + body.angVel * rrz;
  const rvz = body.vel.z - body.angVel * rrx;
  const fLat = fvx * nfx + fvz * nfz;
  const rLat = rvx * nrx + rvz * nrz;
  const fLong = Math.abs(fvx * Math.sin(wA) + fvz * Math.cos(wA));
  const rLong = Math.abs(rvx * sA + rvz * cA);
  const fSlip = Math.atan2(fLat, Math.max(fLong, 0.5));
  const rSlip = Math.atan2(rLat, Math.max(rLong, 0.5));
  // The drift layer rounds the rear curve off from DRIFT_KNEE of the peak, so the tail steps out
  // progressively instead of letting go at once.
  const knee = 1 - (1 - DRIFT_KNEE) * driftWindow;
  const tyre = (lat: number, slip: number, max: number, k: number, soft: number): number => {
    const cancel = Math.abs(lat / k) * 0.92; // never overshoot past zero lateral velocity
    let linear = (Math.abs(slip) / peak) * max;
    if (soft < 1 && linear > soft * max) linear = max * (soft + (1 - soft) * Math.tanh((linear / max - soft) / (1 - soft)));
    return -Math.sign(lat) * Math.min(cancel, linear, max);
  };
  const jF = tyre(fLat, fSlip, maxF, kF, 1);
  const jR = tyre(rLat, rSlip, maxR, kR, knee);
  {
    const jx = nfx * jF + nrx * jR;
    const jz = nfz * jF + nrz * jR;
    body.vel.x += jx * invM;
    body.vel.z += jz * invM;
    body.angVel += invIt * (cfz * jF + crz * jR);
  }
  // >1 means the axle is past its peak (sliding).
  const slideF = Math.abs(fSlip) / peak;
  const slideR = Math.abs(rSlip) / peak;

  // Handbrake yaw kick: an arcade assist that makes handbrake turns and J-turns snap round.
  // The drift layer softens it going forwards (a controlled slide, not a pirouette); in reverse (a
  // J-turn) the full kick stays.
  if (handbrake && Math.abs(tel.steer) > 0.2 && speed > 2.5 && Math.abs(vF) > 0.5) {
    const dir = -Math.sign(vF) * Math.sign(tel.steer);
    const mild = vF > 0 ? 1 - DRIFT_HANDBRAKE_KICK * drift : 1;
    const target = dir * Math.min(3.4, speed * 0.5) * mild;
    if ((target > 0 && body.angVel < target) || (target < 0 && body.angVel > target)) {
      body.angVel += dir * (HANDBRAKE_YAW / spec.inertiaScale) * mild * Math.abs(tel.steer) * Math.min(1, speed / 6) * dt;
    }
  }

  // Drift assist: a forward slide past the limit is turned back into forward speed (the slip angle
  // shrinks, the speed is kept) and the yaw feeding it is damped, so a slide is held and caught,
  // never spun. Below the limit a drift is carried round too (DRIFT_CARRY): the car follows its nose a
  // little, so the slide doesn't run wide into the kerb. A positive yaw rate grows a positive body slip
  // (d beta / d angle = 1).
  if (drift > 0 && vF > 1 && speed > 4) {
    const fx = body.vel.x * sA + body.vel.z * cA;
    const rx = -body.vel.x * cA + body.vel.z * sA;
    const beta = Math.atan2(rx, fx);
    const limit = handbrake ? DRIFT_MAX_SLIP_HANDBRAKE : DRIFT_MAX_SLIP;
    const over = Math.abs(beta) - limit;
    const carry = handbrake ? 0 : Math.abs(beta) * (1 - Math.exp(-DRIFT_CARRY * driftWindow * dt));
    if (over > 0 || carry > 0) {
      const k = over > 0 ? 1 - Math.exp(-DRIFT_HOLD * drift * dt) : 0;
      const nb = beta - Math.sign(beta) * Math.min(Math.abs(beta), Math.max(0, over) * k + carry);
      const v = Math.hypot(fx, rx);
      const nf = v * Math.cos(nb);
      const nr = v * Math.sin(nb);
      body.vel.x = nf * sA - nr * cA;
      body.vel.z = nf * cA + nr * sA;
      if (k > 0 && Math.sign(body.angVel) === Math.sign(beta)) body.angVel *= 1 - k;
    }
  }

  // ------------------------------------------------------------------ yaw stability
  if (steerIn === 0 && !handbrake) {
    // Released the wheel: pull yaw toward what the (counter-steered) wheels ask for, so lift-off
    // slides self-straighten within ~0.6 s instead of carrying on rotating (playtest DRIVING-4).
    const kinematic = Math.abs(vF) > 1 ? (-vF * Math.tan(delta)) / spec.wheelbase : 0;
    const k = 1 - Math.exp(-(spec.yawDamping + 3.5) * dt * clamp01(speed / 6));
    body.angVel += (kinematic - body.angVel) * k;
  }
  body.angVel = clamp(body.angVel, -MAX_YAW_RATE, MAX_YAW_RATE);
  if (speed < 0.15 && throttle === 0) {
    body.vel.x = 0;
    body.vel.z = 0;
    body.angVel *= 0.5;
  }
  if (throttle !== 0 || speed > 0) body.wake();

  // ------------------------------------------------------------------ telemetry
  const vFn = body.vel.x * sA + body.vel.z * cA;
  const vRn = -body.vel.x * cA + body.vel.z * sA;
  tel.forwardSpeed = vFn;
  tel.lateralSpeed = vRn;
  tel.frontSlip = speed > 1 ? fSlip : 0;
  tel.rearSlip = speed > 1 ? rSlip : 0;
  const rawLong = dt > 0 ? (vFn - vF0) / dt : 0;
  tel.longAccel += (rawLong - tel.longAccel) * damp(8, dt);
  tel.latAccel = -vFn * body.angVel; // centripetal, + = toward the right
  const moving = clamp01(speed / 3);
  const skidF = clamp01((slideF - 1.4) / 1.5) * moving;
  let skidR = clamp01((slideR - 1.2) / 1.5) * moving;
  if (handbrake && speed > 3) skidR = Math.max(skidR, clamp01(speed / 12) * 0.85);
  const lockSkid = braking && throttle < -0.9 && speed > 10 ? 0.35 : 0;
  const spin =
    throttle > 0.7 && vFn >= -0.5 && vFn < 8 && spec.accel > 7 ? clamp01((spec.accel - 7) / 4 + 0.2) * (1 - Math.max(vFn, 0) / 8) : 0;
  tel.wheelspin = spin;
  const driveSpin = spin * 0.9;
  const fl = Math.max(skidF, lockSkid, spec.drive === 'front' ? driveSpin : 0);
  const rl = Math.max(skidR, lockSkid, spec.drive === 'rear' ? driveSpin : 0);
  // Outside wheels carry more load and squeal harder.
  const bias = clamp(tel.latAccel / 15, -0.25, 0.25);
  tel.wheelSkid[0] = clamp01(fl * (1 - bias));
  tel.wheelSkid[1] = clamp01(fl * (1 + bias));
  tel.wheelSkid[2] = clamp01(rl * (1 - bias));
  tel.wheelSkid[3] = clamp01(rl * (1 + bias));
  tel.skid = Math.max(tel.wheelSkid[0], tel.wheelSkid[1], tel.wheelSkid[2], tel.wheelSkid[3]);
  tel.braking = braking;
  tel.reversing = vFn < -0.5 && throttle < 0;
  tel.throttle = throttle;
  tel.topSpeed = top;
  const frac = clamp01(Math.abs(vFn) / spec.maxSpeed);
  const g = Math.min(spec.gears, Math.floor(frac * spec.gears) + 1);
  const within = frac * spec.gears - (g - 1);
  tel.gear = tel.reversing ? -1 : g;
  const span = spec.redlineRpm - spec.idleRpm;
  const targetRpm =
    speed < 0.5
      ? spec.idleRpm + span * 0.55 * Math.max(throttle, 0)
      : spec.idleRpm + span * (0.22 + 0.78 * clamp01(within + spin * 0.5 + skidR * 0.15 * Math.max(throttle, 0)));
  tel.rpm += (targetRpm - tel.rpm) * damp(12, dt);
  tel.load = Math.abs(throttle) * mods.power;
}
